import { useState, useRef, useEffect } from 'react'

interface RoyalLogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  animated?: boolean
  showParticles?: boolean
}

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  opacity: number
  size: number
  color: string
}

export default function RoyalLogo({ 
  className = '', 
  size = 'md',
  animated = false,
  showParticles = true
}: RoyalLogoProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [particles, setParticles] = useState<Particle[]>([])
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  
  const sizeClasses = {
    sm: 'h-6 w-auto',
    md: 'h-8 w-auto', 
    lg: 'h-10 w-auto'
  }

  const logoSrc = '/royal.png' // Usando royal.png en lugar del SVG problemático

  const colors = ['#3B82F6', '#06B6D4', '#10B981', '#84CC16', '#22C55E', '#00FFCC', '#0EA5E9']

  const createParticles = () => {
    const newParticles: Particle[] = []
    for (let i = 0; i < 8; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        opacity: Math.random() * 0.8 + 0.2,
        size: Math.random() * 3 + 1,
        color: colors[Math.floor(Math.random() * colors.length)]
      })
    }
    setParticles(newParticles)
  }

  const animateParticles = () => {
    if (!canvasRef.current) return
    
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    setParticles(prevParticles => 
      prevParticles.map(particle => {
        // Update position
        const newX = particle.x + particle.vx
        const newY = particle.y + particle.vy
        
        // Bounce off edges
        let newVx = particle.vx
        let newVy = particle.vy
        
        if (newX <= 0 || newX >= canvas.width) newVx *= -1
        if (newY <= 0 || newY >= canvas.height) newVy *= -1

        // Draw particle
        ctx.beginPath()
        ctx.arc(newX, newY, particle.size, 0, Math.PI * 2)
        ctx.fillStyle = `${particle.color}${Math.floor(particle.opacity * 255).toString(16).padStart(2, '0')}`
        ctx.fill()

        // Add glow effect
        ctx.shadowColor = particle.color
        ctx.shadowBlur = particle.size * 2
        ctx.fill()
        ctx.shadowBlur = 0

        return {
          ...particle,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          opacity: particle.opacity * 0.98 // Gradual fade
        }
      }).filter(particle => particle.opacity > 0.1)
    )

    if (isHovered) {
      animationRef.current = requestAnimationFrame(animateParticles)
    }
  }

  useEffect(() => {
    if (isHovered && showParticles) {
      createParticles()
      animateParticles()
    } else {
      setParticles([])
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isHovered, showParticles])

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.parentElement?.getBoundingClientRect()
      if (rect) {
        canvas.width = rect.width
        canvas.height = rect.height
      }
    }
  }, [])

  return (
    <div 
      className={`relative flex-shrink-0 ${sizeClasses[size]}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showParticles && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none z-0"
          style={{ width: '100%', height: '100%' }}
        />
      )}
      <img 
        src={logoSrc}
        className={`relative z-10 w-full h-full object-contain royal-logo royal-logo-interactive transition-all duration-300 ${className}`}
      />
    </div>
  )
}
